// Index route: sends signed-in users to their last region (or the first selectable one) and signed-out users to sign-in; before any grid code is loaded it says so and still offers user administration (admins), the account page and sign-out, so a fresh deployment is not a dead end.
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { CircleUserRoundIcon, GlobeIcon, LogOutIcon, UserPlusIcon } from "lucide-react";

import { metaListJurisdictionsOptions } from "@/api/__generated__/@tanstack/react-query.gen";
import { useAuth } from "@/app/auth/useAuth";
import { canAccess } from "@/app/nav";
import { defaultRegion, regionParam } from "@/app/region";
import { meQueryOptions } from "@/app/session";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { notify } from "@/components/ui/sonner";
import { authProblemCopy } from "@/features/auth/problems";

export const Route = createFileRoute("/")({
  beforeLoad: async ({ context }) => {
    if (context.auth.status !== "authenticated") {
      throw redirect({ to: "/signin" });
    }
    const jurisdictions = await context.queryClient.query({
      ...metaListJurisdictionsOptions(),
      staleTime: "static",
    });
    const region = defaultRegion(jurisdictions);
    if (region !== undefined) {
      throw redirect({ to: "/$region", params: { region: regionParam(region) }, replace: true });
    }
  },
  component: NoRegion,
});

function NoRegion() {
  const auth = useAuth();
  const me = useQuery(meQueryOptions()).data;
  const admin = me !== undefined && canAccess(me.roles, "A");
  const signOut = () => {
    void auth.signOut().catch((error: unknown) => {
      notify("You're still signed in", authProblemCopy(error).description);
    });
  };
  return (
    <main id="main" className="grid min-h-dvh place-items-center bg-background p-6">
      <EmptyState
        icon={GlobeIcon}
        title="No grid code is loaded yet"
        description="Register a grid code with gridcode-kg source add-file and run ingest run, then reload this page."
        action={
          <div className="flex flex-wrap justify-center gap-2">
            {admin ? (
              <Button asChild>
                <Link to="/admin/users">
                  <UserPlusIcon aria-hidden />
                  Invite people
                </Link>
              </Button>
            ) : null}
            <Button asChild variant="outline">
              <Link to="/account">
                <CircleUserRoundIcon aria-hidden />
                Your account
              </Link>
            </Button>
            <Button variant="outline" onClick={signOut}>
              <LogOutIcon aria-hidden />
              Sign out
            </Button>
          </div>
        }
      />
    </main>
  );
}
